import { AssetClass } from '@/entities/assets'
import { TagClass } from '@/entities/tags'
import { TransactionClass } from '@/entities/transactions'

import { TagRef } from '@/entities/tags/utils/types'
import { WizardAccountMode, WizardMark, WizardValues } from './types'

import { ASSET_FIELD } from '@/entities/assets/utils/const'
import { TRANSACTION_FIELD } from '@/entities/transactions/utils/const'
import { ACCOUNT_MODE_FIELD } from './const'

import { useAssetPricesStore } from '@/store/assets/prices'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useRepositoriesStore } from '@/store/repositories'

/*
 * What every wizard's create is built from.
 *
 * The steps a flow performs are its own — a loan creates one holding, a
 * financed property creates two and an account to borrow from — so each folder
 * writes its own `create.ts` and this file holds the pieces they share. The
 * alternative, a declarative plan on the definition, would have needed a
 * mapping language for exactly six call sites; six small explicit functions
 * read better and let a flow that needs something unusual just write it.
 *
 * Everything here goes through the entity classes rather than the stores. They
 * own the transaction-entry wiring, the sign rule on an amount and the
 * bunch/DuckDB updates that keep the rest of the app in step — reaching past
 * them into `api.post` means reimplementing all three.
 *
 * Nothing here rolls back. A step that throws stops the run and leaves what
 * already landed, which is what the legacy wizard does and what the API allows:
 * there is no transaction spanning these calls, and a compensating delete can
 * fail too. The dialog reports the failure and keeps the form, so the user can
 * see what happened rather than being told a clean lie.
 */

/**
 * Tag ids for everything applied, creating the pairs the repository lacks.
 *
 * `id` is the whole question: a tag that carries one already exists and is
 * linked, and one that does not is created first. Resolved upstream against the
 * catalogue by `withTagId`, never claimed by whatever suggested the tag.
 *
 * Sequential rather than `Promise.all`: two applied tags can name the same new
 * dimension, and firing both at once would race to create it twice.
 */
export const ensureTagIds = async (tags: TagRef[] = []): Promise<string[]> => {
  const ids: string[] = []

  for (const tag of tags) {
    if (tag.id) {
      ids.push(tag.id)
      continue
    }

    const created = new TagClass({ tag_name: tag.tag_name, tag_value: tag.tag_value })
    await created.store()
    ids.push(created.id)
  }

  return ids
}

/**
 * The account a holding will hang off: the one that was picked, or a new one.
 *
 * An account is an asset like any other here, so this is `createAsset` with the
 * name the step gave it — the separation is what the account is *for*, not what
 * it is made of.
 */
export const ensureAccount = async (params: {
  /** An account the repository already has. Given, nothing is created. */
  id?: string
  name: string
  type: string
  currency: string
}): Promise<string> => {
  if (params.id) return params.id

  const account = await createAsset({
    name: params.name,
    type: params.type,
    currency: params.currency,
  })

  return account.id
}

/**
 * The account for a flow whose ownership step collects it.
 *
 * The mode is the choice the step asked, and the schema has already enforced
 * that whichever half it names is filled — so this reads it rather than
 * re-deciding from which fields happen to be empty.
 *
 * Writes the resolved id back onto `values.ownerAccountId`. A newly created
 * account has none yet, and `create()` only hands the dialog back the
 * holdings it made — this is the one place that also knows which account
 * they ended up under, so the done screen reads it from here rather than
 * needing its own copy of this same resolution.
 */
export const ensureOwnerAccount = async (values: WizardValues): Promise<string> => {
  const accountId = await ensureAccount({
    id: values[ACCOUNT_MODE_FIELD] === WizardAccountMode.NEW
      ? undefined
      : values.ownerAccountId,
    name: values.ownerAccountName,
    type: values.ownerAccountType,
    currency: values.currency,
  })

  values.ownerAccountId = accountId

  return accountId
}

/**
 * The values recorded on the valuation step, as rows against the asset.
 *
 * After the holding, never before: a price needs an asset id, and a row written
 * against an asset whose transaction then fails would leave a value for a
 * position nobody holds.
 *
 * `fromPurchase` is dropped here — it is how the step labels the row it added
 * from the wizard's own purchase price, and `asset_prices` has no such column.
 *
 * Sequential, like `ensureTagIds`: each row deprecates the analytics cache, so
 * firing them together only repeats that work.
 */
export const createMarks = async (assetId: string, marks: WizardMark[] = []): Promise<void> => {
  const pricesStore = useAssetPricesStore()

  for (const mark of marks) {
    await pricesStore.store({
      asset_id: assetId,
      date: mark.date,
      close: mark.close,
      source: mark.source,
    })
  }
}

/**
 * One asset, stored.
 *
 * The ticker mirrors the name by default. Every wizard here collects a name
 * and none of them collects a ticker, but `AssetClass.validate` requires one
 * on anything that is not an income or expense account — and the legacy
 * wizards fill it the same way, from the same field. `ticker` overrides that
 * default for the one caller that cannot use it: a fund split across
 * investors writes the same name for every bundle on purpose (it is the same
 * fund), so the ticker is what has to carry the difference — see
 * PrivateFund/create.ts.
 */
export const createAsset = async (params: {
  name: string
  type: string
  currency: string
  description?: string
  /** Tag ids, already resolved and created — see `ensureTagIds`. */
  tagIds?: string[]
  /** Defaults to `name`. See above for the one caller that sets it. */
  ticker?: string
}): Promise<AssetClass> => {
  /*
   * The repository this belongs to, stated rather than assumed: `POST /assets`
   * takes what it is given — unlike `POST /transactions`, which scopes itself —
   * so an unset repository would create the asset wherever the API decided to
   * put it. Bail instead, per the repo-wide scoping rule.
   */
  const repositoryId = useRepositoriesStore().getCurrentId
  if (!repositoryId) throw Error('No repository is selected')

  const asset = new AssetClass({
    name: params.name,
    ticker: params.ticker ?? params.name,
    type: params.type,
    currency: params.currency,
    description: params.description ?? '',
    repository_id: repositoryId,
  })

  if (params.tagIds?.length) {
    asset.field<string[]>(ASSET_FIELD.TAGS).value = params.tagIds
  }

  await asset.store()

  return asset
}

/**
 * One transaction against an asset that already exists — a holding's own
 * value leg, factored out of `createHolding` so a follow-up cash flow (a
 * capital call, a fee, a distribution — see PrivateFund/create.ts) can post
 * the same shape against a fund the run already created, without creating a
 * second asset alongside it.
 *
 * Both legs are pasted rather than assigned: `pasteAmount` is what applies the
 * repository's sign rule for this transaction type, which is the difference
 * between a purchase price the API accepts and one it rejects.
 *
 * `check: false` on the ids for the same reason the legacy wizard passes it —
 * the checks read the account and asset out of the bunch store to validate the
 * type against the transaction settings, and an asset stored a moment ago is
 * exactly the case they were not written for.
 */
export const postTransaction = async (params: {
  /** The account the transaction hangs off. */
  accountId: string
  /** The asset it is against — already stored, unlike `createHolding`'s. */
  assetId: string
  transactionType: string
  currency: string
  amount: number
  /** ISO, as the schema enforces. */
  date: string
  /**
   * The transaction's own description — not the asset's. Absent by default:
   * most callers have nothing of their own to say here, and an empty string
   * on a transaction record is the same as never having set the field.
   */
  description?: string
}): Promise<void> => {
  /*
   * The currency the value leg is denominated in, as an asset of its own.
   * Resolved before anything is written, not after: this is the one input that
   * can be missing on a form the schema called complete, and finding out once
   * the asset is stored leaves a holding with no transaction against it. The
   * run either does both or neither.
   *
   * Wizard.vue is what makes sure the assets are loaded to look this up in.
   */
  const currencyId = useAssetsBunchStore().getElementByTicker(params.currency)?.id
  if (!currencyId) {
    throw Error(`This repository has no asset for the currency ${params.currency}`)
  }

  const transaction = new TransactionClass(
    { date: params.date },
    false,
    // Loose validation, as the legacy wizard builds it: the entries are filled
    // by the pastes below, and validating an empty transaction on construction
    // only produces errors for values that are one line away.
    false,
  )

  /*
   * Set through the reactive field, not the constructor's data — only that
   * path runs `prepareFields`, which is what shapes `entries` to this type's
   * own count (two for most, three for the income types: cash dividend, cash
   * interest, current income, realized gain or loss) and pre-fills each one,
   * including the third entry's fixed category account. Passing `entries` in
   * the constructor, as this used to, freezes it at two regardless of type.
   */
  transaction.field(TRANSACTION_FIELD.TYPE).value = params.transactionType
  if (params.description) {
    transaction.field(TRANSACTION_FIELD.DESCRIPTION).value = params.description
  }

  transaction.pasteAccountId1(params.accountId, { check: false })
  transaction.pasteAssetId1(params.assetId, { check: false })
  transaction.pasteAssetId2(currencyId, { check: false })

  /*
   * The holding leg's own quantity — one of the asset, never anything else,
   * so it is always "1" rather than a value read off the form. `prepareFields`
   * only pre-fills this where the repository's own entry carries a default —
   * subscribe and fund/commit's own entry does — but add position's own
   * entry requires a positive amount with no default, so it is left null
   * until pasted here explicitly, the same as the old hardcoded 2-entry
   * array used to set it directly. Skipped for a transaction type whose
   * first entry carries no amount at all (add liability's own loan leg) —
   * pasting there would reject on "Empty amount".
   */
  if (!transaction.emptyAmount1) {
    await transaction.pasteAmount1('1')
  }

  // `pasteAmount2` is the value leg — entry 1 — and it is what applies the
  // repository's sign rule for this transaction type.
  await transaction.pasteAmount2(String(params.amount))
  await transaction.store()
}

/**
 * An asset and the transaction that puts it on the books — the unit every
 * wizard creates at least one of.
 *
 * The asset is stored first because the transaction has to name its id — see
 * `postTransaction` for the leg itself. The currency is still checked before
 * either write, not left to `postTransaction`'s own check: failing after the
 * asset is already stored would leave a holding with no transaction against
 * it, and the run either does both or neither.
 */
export const createHolding = async (params: {
  /** The account the record hangs off. */
  accountId: string
  transactionType: string
  name: string
  type: string
  currency: string
  /** The asset's own description — see `createAsset`. */
  description?: string
  amount: number
  /** ISO, as the schema enforces. */
  date: string
  tagIds?: string[]
  /** Passed straight through to createAsset — see there for who sets it. */
  ticker?: string
  /**
   * The transaction's own description, distinct from the asset's above — see
   * `postTransaction`. Absent by default; the mortgage half of
   * RealEstateWithFinancing is the one caller that sets it today.
   */
  transactionDescription?: string
}): Promise<AssetClass> => {
  if (!useAssetsBunchStore().getElementByTicker(params.currency)?.id) {
    throw Error(`This repository has no asset for the currency ${params.currency}`)
  }

  const asset = await createAsset(params)

  await postTransaction({
    accountId: params.accountId,
    assetId: asset.id,
    transactionType: params.transactionType,
    currency: params.currency,
    amount: params.amount,
    date: params.date,
    description: params.transactionDescription,
  })

  return asset
}
